import { AlertTriangle } from 'lucide-react';

/**
 * ConfirmDialog - Reusable modal dialog for confirmation actions
 *
 * Features:
 * - Centered modal with backdrop
 * - Icon support (default: warning triangle)
 * - Customizable title, message, and button labels
 * - Emerald confirm button, gray cancel button
 * - Keyboard support (Enter to confirm, Escape to cancel)
 * - Click outside to cancel
 *
 * @param {boolean} isOpen - Whether the dialog is visible
 * @param {function} onConfirm - Callback when confirm button is clicked
 * @param {function} onCancel - Callback when cancel button is clicked or dialog is dismissed
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message/description
 * @param {string} confirmLabel - Confirm button label (default: "Bestätigen")
 * @param {string} cancelLabel - Cancel button label (default: "Abbrechen")
 * @param {string} variant - Button variant: "danger" (red) or "primary" (emerald) (default: "danger")
 * @param {React.Component} icon - Icon component (default: AlertTriangle)
 */
const ConfirmDialog = ({
  isOpen,
  onConfirm,
  onCancel,
  title = 'Bestätigen',
  message = 'Sind Sie sicher?',
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  variant = 'danger',
  icon: Icon = AlertTriangle,
}) => {
  // Don't render if not open
  if (!isOpen) return null;

  // Handle keyboard events
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation(); // Prevent parent dialogs from also closing
      onCancel();
    } else if (e.key === 'Enter') {
      e.stopPropagation();
      onConfirm();
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      e.stopPropagation(); // Prevent parent dialogs from also closing
      onCancel();
    }
  };

  // Button styles based on variant
  const confirmButtonClass =
    variant === 'danger'
      ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
      : 'bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500';

  return (
    /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- role="dialog" needs onKeyDown for Escape close */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      aria-describedby="dialog-message"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 animate-fadeIn">
        {/* Dialog Content */}
        <div className="p-6">
          {/* Icon and Title */}
          <div className="flex items-start gap-4">
            <div
              className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                variant === 'danger' ? 'bg-red-100' : 'bg-emerald-100'
              }`}
            >
              <Icon
                size={24}
                className={variant === 'danger' ? 'text-red-600' : 'text-emerald-600'}
              />
            </div>
            <div className="flex-1">
              <h3
                id="dialog-title"
                className="text-lg font-semibold text-gray-900 mb-2"
              >
                {title}
              </h3>
              <p id="dialog-message" className="text-sm text-gray-600">
                {message}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex gap-3 justify-end border-t border-gray-200">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${confirmButtonClass}`}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- modal confirm button should receive focus when dialog opens
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
